import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { getCookie } from "../../Utils/cookies";

// Dropdown arrow icon component
const DropdownArrow = ({ isOpen }) => (
  <svg
    className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

// Simple inline notification component
const Notification = ({ message, type, onClose }) => {
  if (!message) return null;
  const bgColor =
    type === "error"
      ? "bg-red-100 border-red-400 text-red-700"
      : "bg-green-100 border-green-400 text-green-700";
  return (
    <div
      className={`${bgColor} border px-4 py-3 rounded relative mb-4`}
      role="alert"
    >
      <span className="block sm:inline">{message}</span>
      <button
        onClick={onClose}
        className="absolute top-0 bottom-0 right-0 px-4 py-3"
      >
        <span className="text-xl">&times;</span>
      </button>
    </div>
  );
};

const ITEMS_PER_PAGE = 50;
const SEARCH_DEBOUNCE_MS = 400;

const MhAssignInstallation = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const states = [
    "Maharashtra",
    "Haryana",
    "Punjab",
    "Rajasthan",
    "Chhattisgarh",
  ];
  const [selectedState, setSelectedState] = useState("Maharashtra");
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const [servicePersons, setServicePersons] = useState([]);
  const [loadingServicePersons, setLoadingServicePersons] = useState(false);
  const [servicePersonError, setServicePersonError] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [currentActivityId, setCurrentActivityId] = useState(null);
  const [assigning, setAssigning] = useState(false);

  // Search state (raw input + debounced value actually sent to the API)
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Notification state
  const [notification, setNotification] = useState({ message: "", type: "" });

  // Safely parse empData cookie
  let empData = {};
  try {
    empData = JSON.parse(getCookie("empData") || "{}");
  } catch (e) {
    console.error("Invalid empData cookie", e);
  }
  const empId = empData?.empId;

  // Server-driven pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Debounce the search box so we don't hit the API on every keystroke
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Reset to page 1 whenever the filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedState, debouncedSearchTerm]);

  // Fetch the current page of activities from the server.
  // Re-runs whenever page, state, or (debounced) search term changes.
  const fetchActivities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/all-farmer-activities`,
        {
          params: {
            page: currentPage,
            limit: ITEMS_PER_PAGE,
            state: selectedState || undefined,
            farmerSaralId: debouncedSearchTerm || undefined,
          },
        },
      );
      if (response.data.success) {
        setActivities(response.data.data);
        const pagination = response.data.pagination || {};
        setTotalCount(pagination.total ?? response.data.data.length);
        setTotalPages(
          pagination.totalPages ??
            Math.max(1, Math.ceil((pagination.total ?? 0) / ITEMS_PER_PAGE)),
        );
      } else {
        throw new Error(response.data.message || "Failed to fetch data");
      }
    } catch (err) {
      setError(err.message);
      setNotification({ message: err.message, type: "error" });
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedState, debouncedSearchTerm]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Fetch service persons for the selected state
  useEffect(() => {
    const fetchServicePersons = async () => {
      if (!selectedState) return;
      setLoadingServicePersons(true);
      setServicePersonError(null);
      setServicePersons([]);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/state-wise-service-persons?state=${selectedState}`,
        );
        if (response.data.success) {
          setServicePersons(response.data.data);
        } else {
          throw new Error(
            response.data.message || "Failed to fetch service persons",
          );
        }
      } catch (err) {
        setServicePersonError(err.message);
        setNotification({ message: err.message, type: "error" });
      } finally {
        setLoadingServicePersons(false);
      }
    };

    fetchServicePersons();
  }, [selectedState]);

  // ---------- Handlers ----------
  const toggleStateDropdown = () => setIsStateDropdownOpen((prev) => !prev);
  const handleSelectState = (state) => {
    setSelectedState(state);
    setIsStateDropdownOpen(false);
  };

  const handleAssignClick = (activityId) => {
    if (!empId) {
      setNotification({
        message: "Employee information missing. Please log in again.",
        type: "error",
      });
      return;
    }
    setCurrentActivityId(activityId);
    setShowAssignModal(true);
  };

  const handlePersonSelect = async (person) => {
    if (!currentActivityId) {
      setNotification({
        message: "Missing required information.",
        type: "error",
      });
      return;
    }

    const activity = activities.find((act) => act._id === currentActivityId);
    if (!activity) {
      setNotification({ message: "Activity not found.", type: "error" });
      return;
    }

    setAssigning(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/assign-installer`,
        {
          farmerActivityId: currentActivityId,
          farmerSaralId: activity.farmerSaralId,
          installerId: person._id,
          updatedByEmp: empId,
        },
      );

      if (response.data.success) {
        setActivities((prev) =>
          prev.map((act) =>
            act._id === currentActivityId
              ? {
                  ...act,
                  assignedPerson: { name: person.name, empId: person.empId },
                }
              : act,
          ),
        );
        setNotification({
          message: `Assigned ${person.name} to activity`,
          type: "success",
        });
        closeModal();
      } else {
        throw new Error(response.data.message || "Assignment failed");
      }
    } catch (err) {
      setNotification({
        message: "Error assigning: " + err.message,
        type: "error",
      });
    } finally {
      setAssigning(false);
    }
  };

  const closeModal = () => {
    setShowAssignModal(false);
    setCurrentActivityId(null);
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const clearNotification = () => setNotification({ message: "", type: "" });

  // ---------- Loading & Error ----------
  if (loading && activities.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !activities.length) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  const rangeStart = totalCount === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const rangeEnd = Math.min(currentPage * ITEMS_PER_PAGE, totalCount);

  return (
    // Full viewport height, no outer scrollbar
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="container mx-auto px-4 py-4 flex-1 flex flex-col min-h-0">
        <h1 className="text-2xl font-bold mb-4 flex-shrink-0">
          Maharashtra - Assign Installation
        </h1>

        <Notification
          message={notification.message}
          type={notification.type}
          onClose={clearNotification}
        />

        {/* Filters row: State dropdown + Search input */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6 flex-shrink-0">
          {/* State Selection Dropdown */}
          <div className="relative w-full sm:w-64">
            <div
              className="flex justify-between items-center w-full p-2 border border-gray-300 rounded bg-white cursor-pointer"
              onClick={toggleStateDropdown}
            >
              <span>{selectedState || "Select state"}</span>
              <DropdownArrow isOpen={isStateDropdownOpen} />
            </div>
            {isStateDropdownOpen && (
              <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 max-h-60 overflow-y-auto">
                {states.map((state) => (
                  <div
                    key={state}
                    className="p-2 hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleSelectState(state)}
                  >
                    {state}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Search by Saral ID */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by Saral ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Activities Table with internal scroll only */}
        {!loading && activities.length === 0 ? (
          <p className="text-gray-600 flex-shrink-0">
            No activities found for {selectedState}
            {debouncedSearchTerm && ` matching "${debouncedSearchTerm}"`}.
          </p>
        ) : (
          <>
            {/* Scrollable table container - takes remaining space */}
            <div className="border border-gray-200 rounded flex-1 min-h-0 flex flex-col relative">
              {loading && (
                <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}
              <div className="overflow-x-auto overflow-y-auto flex-1">
                <table className="min-w-full bg-white border-collapse">
                  <thead className="sticky top-0 bg-gray-100 z-10 shadow-sm">
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 border-b">
                        Farmer Name
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 border-b">
                        Saral ID
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 border-b">
                        System
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 border-b">
                        Warehouse
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 border-b">
                        Assigned To
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 border-b">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((activity) => {
                      const farmer = activity.farmerDetails || {};
                      const warehouse = activity.warehouseId || {};
                      const system = activity.systemId || {};
                      return (
                        <tr key={activity._id} className="hover:bg-gray-50">
                          <td className="py-3 px-4 border-b text-sm">
                            {farmer.farmerName || "N/A"}
                          </td>
                          <td className="py-3 px-4 border-b text-sm">
                            {activity.farmerSaralId || "N/A"}
                          </td>
                          <td className="py-3 px-4 border-b text-sm">
                            {system.systemName || "N/A"}
                          </td>
                          <td className="py-3 px-4 border-b text-sm">
                            {warehouse.warehouseName || "N/A"}
                          </td>
                          <td className="py-3 px-4 border-b text-sm">
                            {activity.assignedPerson?.name || "Not Assigned"}
                          </td>
                          <td className="py-3 px-4 border-b text-sm">
                            <button
                              onClick={() => handleAssignClick(activity._id)}
                              className="bg-yellow-400 text-dark font-bold py-1 px-3 rounded text-xs"
                            >
                              Assign
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Controls */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 gap-2 flex-shrink-0">
              <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
                Showing{" "}
                <span className="font-medium">{rangeStart}</span> to{" "}
                <span className="font-medium">{rangeEnd}</span> of{" "}
                <span className="font-medium">{totalCount}</span> results
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center sm:justify-end space-x-2">
                  <button
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1 || loading}
                    className={`px-3 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded ${
                      currentPage === 1 || loading
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm bg-blue-50 text-blue-600 rounded">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages || loading}
                    className={`px-3 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded ${
                      currentPage === totalPages || loading
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Assignment Modal (unchanged) */}
        {showAssignModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-xl font-semibold mb-4">
                Select Service Person
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                State: <span className="font-medium">{selectedState}</span>
              </p>

              {loadingServicePersons ? (
                <div className="flex justify-center items-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : servicePersonError ? (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {servicePersonError}
                </div>
              ) : servicePersons.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No service persons found for this state.
                </p>
              ) : (
                <>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded mb-4">
                    {servicePersons.map((person) => (
                      <div
                        key={person._id}
                        className={`p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 ${
                          assigning ? "pointer-events-none opacity-50" : ""
                        }`}
                        onClick={() => !assigning && handlePersonSelect(person)}
                      >
                        <p className="font-medium">{person.name}</p>
                        <p className="text-sm text-gray-600">{person.empId}</p>
                      </div>
                    ))}
                  </div>
                  {assigning && (
                    <div className="flex justify-center mb-4">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-end">
                <button
                  onClick={closeModal}
                  disabled={assigning}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded mr-2 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MhAssignInstallation;